import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Camera, Check, RefreshCw, Loader2, CameraOff } from "lucide-react";
import { toast } from "sonner";

export default function Selfie() {
  const nav = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [camErr, setCamErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/attendance/today").then((r) => { if (!r.data.required || r.data.marked) nav("/dashboard", { replace: true }); else setReady(true); }).catch(() => setReady(true));
  }, [nav]);

  useEffect(() => {
    if (!ready || shot) return;
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } }, audio: false })
      .then((s) => { if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; } streamRef.current = s; if (videoRef.current) videoRef.current.srcObject = s; })
      .catch((e) => setCamErr(e?.name === "NotAllowedError" ? "Camera permission denied" : "Camera not available"));
    if (!navigator.mediaDevices) setCamErr("Camera not supported in this browser");
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  }, [ready, shot]);

  const capture = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    const size = Math.min(v.videoWidth, v.videoHeight);
    const c = document.createElement("canvas"); c.width = c.height = 640;
    const ctx = c.getContext("2d");
    ctx.translate(640, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, (v.videoWidth - size) / 2, (v.videoHeight - size) / 2, size, size, 0, 0, 640, 640);
    c.toBlob((b) => { setShot({ blob: b, url: URL.createObjectURL(b) }); streamRef.current?.getTracks().forEach((t) => t.stop()); }, "image/jpeg", 0.85);
  };

  const submit = async (noPhoto) => {
    setBusy(true);
    try {
      if (noPhoto) await api.post("/attendance/selfie", null, { params: { no_photo: true, reason: camErr || "camera unavailable" } });
      else { const fd = new FormData(); fd.append("file", shot.blob, "selfie.jpg"); await api.post("/attendance/selfie", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      toast.success(noPhoto ? "Attendance marked (no photo)" : "Attendance marked — have a great shift!");
      nav("/dashboard", { replace: true });
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  if (!ready) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  return (
    <div className="min-h-screen bg-[#00558f] text-white flex flex-col items-center justify-between py-8 px-5" data-testid="selfie-page">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Attendance</div>
        <h1 className="text-2xl font-display font-bold mt-1">Good to see you, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-white/80 mt-1">Take a quick selfie to mark today's attendance.</p>
      </div>

      <div className="relative w-[78vw] max-w-[360px] aspect-square rounded-full overflow-hidden border-4 border-[color:var(--bpcl-orange)] shadow-2xl bg-black/40 grid place-items-center" data-testid="selfie-frame">
        {shot ? <img src={shot.url} alt="Your selfie" className="w-full h-full object-cover" data-testid="selfie-preview" />
          : camErr ? <div className="text-center px-6"><CameraOff className="w-10 h-10 mx-auto mb-2 text-white/70" /><div className="text-sm font-semibold">{camErr}</div><div className="text-xs text-white/70 mt-1">You can still mark attendance without a photo.</div></div>
          : <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" data-testid="selfie-video" />}
      </div>

      <div className="w-full max-w-[360px] space-y-3">
        {shot ? (
          <>
            <Button onClick={() => submit(false)} disabled={busy} className="w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-lg font-bold" data-testid="selfie-confirm">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-2" /> Looks good — Confirm</>}</Button>
            <Button variant="outline" onClick={() => setShot(null)} disabled={busy} className="w-full h-12 rounded-2xl bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="selfie-retake"><RefreshCw className="w-4 h-4 mr-2" /> Retake</Button>
          </>
        ) : camErr ? (
          <Button onClick={() => submit(true)} disabled={busy} className="w-full h-14 rounded-2xl bg-[color:var(--bpcl-orange)] text-slate-900 hover:brightness-105 text-base font-bold" data-testid="selfie-no-photo">Continue without photo</Button>
        ) : (
          <button onClick={capture} className="mx-auto block w-20 h-20 rounded-full bg-white border-[6px] border-white/40 shadow-xl active:scale-95 transition-transform grid place-items-center" aria-label="Capture" data-testid="selfie-capture"><Camera className="w-8 h-8 text-[#00558f]" /></button>
        )}
      </div>
    </div>
  );
}
